import React, { useEffect, useRef, useState } from "react";
import { Loader2, Printer, QrCode } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/button";
import QRCodePreview from "../../../components/ui/QRCodePreview";
import BatchFormFields from "./BatchFormFields";
import { useApiMutation } from "../../../hooks/useApiMutation";
import { batchApi } from "../../../api/batchApi";
import { downloadBlob } from "../../../services/fileDownload";
import { useToast } from "../../../components/ui/toast/ToastProvider";
import useBatchFormOptions from "./useBatchFormOptions";
import { changeBatchField, resolveBatchInput, buildBatchPayload, emptyBatchValues, printBatchQr, unwrapBatch, validateBatchValues } from "./batchFormUtils";

const CreateBatchModal = ({ open, onClose, onCreated, rawMaterialOptions, partOptions, selectedPlantId, initialContext }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [values, setValues] = useState(emptyBatchValues);
  const [createdBatch, setCreatedBatch] = useState(null);
  const [errors, setErrors] = useState({});
  const qrRef = useRef(null);
  const submitting = useRef(false);
  const generation = useRef(0);
  const { run: createBatch, isLoading, error, reset } = useApiMutation(batchApi.create);
  const { run: downloadQr, isLoading: isDownloadingQr } = useApiMutation(batchApi.downloadQrZip);
  const options = useBatchFormOptions(open, selectedPlantId, partOptions, rawMaterialOptions, values.raw_material);
  const resolvedValues = resolveBatchInput(values, options.inputRecords, options.partOptions, options.materialOptions);

  useEffect(() => {
    generation.current += 1;
    setValues(emptyBatchValues);
    setCreatedBatch(null);
    setErrors({});
    reset();
  }, [open, selectedPlantId, reset]);

  const handleClose = () => {
    if (!submitting.current) onClose();
  };
  const handleChange = (name, value) => {
    setValues((current) => changeBatchField(current, name, value));
    setErrors({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current || createdBatch || !selectedPlantId || options.isLoading || options.isLoadingInputs || options.error) return;
    const validation = validateBatchValues(resolvedValues, options.partOptions, options.materialOptions, options.inputRecords);
    setErrors(validation);
    if (Object.keys(validation).length) return;
    submitting.current = true;
    const requestGeneration = generation.current;
    try {
      const context = Object.fromEntries(["machine_id", "machine_type", "line_id", "shift_id"]
        .filter((key) => initialContext?.[key]).map((key) => [key, initialContext[key]]));
      const created = unwrapBatch(await createBatch({
        ...buildBatchPayload(resolvedValues, options.partOptions),
        ...context,
        plant_id: selectedPlantId,
        status: "CREATED",
      }));
      if (requestGeneration !== generation.current) return;
      setCreatedBatch(created);
      onCreated?.(created);
      toast.success("Batch created and QR code generated.");
    } catch (err) {
      if (requestGeneration === generation.current) toast.error(err?.message || "Unable to create batch.");
    } finally {
      submitting.current = false;
    }
  };

  const handlePrint = () => {
    try { printBatchQr(qrRef.current, createdBatch); }
    catch (err) { toast.error(err.message); }
  };
  const handleDownloadQr = async () => {
    const uid = createdBatch?.uid ?? createdBatch?.id;
    if (!uid) return;
    try {
      const blob = await downloadQr({ ids: [uid] });
      downloadBlob(blob, `${createdBatch.batch_id}-qr.zip`);
    } catch (err) {
      toast.error(err?.message || "Unable to download QR code.");
    }
  };
  const qrValue = createdBatch?.qr_value || (createdBatch?.batch_id
    ? `${createdBatch.plant_id || selectedPlantId}_${createdBatch.batch_id}` : "");

  return (
    <Modal open={open} onClose={handleClose} title="Create New Batch" size="lg">
      <p className="-mt-2 mb-4 text-sm text-slate-500">Enter production details to generate a batch QR code.</p>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_200px]">
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <BatchFormFields values={resolvedValues} onChange={handleChange} rawMaterialOptions={options.materialOptions}
            partOptions={options.partOptions} inputRecords={options.inputRecords} loadingInputs={options.isLoadingInputs} errors={errors} loading={options.isLoading} disabled={isLoading || Boolean(createdBatch)} />
          {!selectedPlantId && <p className="text-sm text-amber-600">Select a plant from the top bar to create a batch.</p>}
          {options.error && <p role="alert" className="text-sm text-rose-600">Unable to load materials, input batches or linked items. <button type="button" onClick={options.refetch} className="underline">Retry</button></p>}
          {error && <p role="alert" className="text-sm text-rose-600">{error.message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>{createdBatch ? "Done" : "Cancel"}</Button>
            {!createdBatch && <Button type="submit" disabled={isLoading || options.isLoading || options.isLoadingInputs || Boolean(options.error) || !selectedPlantId}>
              {isLoading ? "Generating…" : "Generate QR"}
            </Button>}
          </div>
        </form>

        <div aria-live="polite" className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 p-4 text-center">
          {createdBatch ? <>
            <div ref={qrRef}><QRCodePreview value={qrValue} displayValue={createdBatch.batch_id} label={createdBatch.batch_id} size={132} showValue /></div>
            <p className="mt-3 text-xs text-emerald-600">QR code for Batch <span className="font-semibold">{createdBatch.batch_id}</span> has been generated successfully.</p>
            <div className="mt-3 flex w-full flex-col gap-2">
              <Button type="button" size="sm" onClick={handlePrint}><Printer className="mr-1 h-4 w-4" />Print QR Code</Button>
              <Button type="button" size="sm" variant="outline" disabled={isDownloadingQr || !(createdBatch.id || createdBatch.uid)} onClick={handleDownloadQr}>{isDownloadingQr ? "Downloading…" : "Download QR"}</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => {
                onClose();
                navigate(`/setting/batch/${encodeURIComponent(createdBatch.uid || createdBatch.id || createdBatch.batch_id)}`);
              }}>View Batch Details</Button>
            </div>
          </> : <>
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-lightSecondary text-brand">
              {isLoading ? <Loader2 className="h-8 w-8 animate-spin" /> : <QrCode className="h-8 w-8" />}
            </div>
            <p className="mt-3 text-xs text-slate-500">{isLoading ? "Your batch QR code is being generated. This may take a few moments." : "QR Code Preview — Pending Generation"}</p>
          </>}
        </div>
      </div>
    </Modal>
  );
};

export default CreateBatchModal;
